import React, { ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faExclamationTriangle, faInfoCircle, faCheckCircle } from '@fortawesome/free-solid-svg-icons';

export type UiAlertTone = 'amber' | 'red' | 'green' | 'blue' | 'emerald' | 'gray';

const TONE_CLASS: Record<UiAlertTone, string> = {
  amber:   'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300',
  red:     'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-400',
  green:   'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300',
  blue:    'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300',
  emerald: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300',
  gray:    'bg-gray-50 dark:bg-slate-700/50 border-gray-200 dark:border-slate-600 text-gray-600 dark:text-slate-300',
};

const DEFAULT_ICON: Record<UiAlertTone, IconDefinition> = {
  amber: faExclamationTriangle,
  red: faExclamationTriangle,
  green: faCheckCircle,
  blue: faInfoCircle,
  emerald: faInfoCircle,
  gray: faInfoCircle,
};

interface UiAlertProps {
  tone?: UiAlertTone;
  /** ไอคอนซ้าย — ค่าเริ่มต้นตามโทน; ส่ง null เพื่อไม่แสดง */
  icon?: IconDefinition | null;
  iconClassName?: string;
  className?: string;
  children: ReactNode;
}

/**
 * กล่องข้อความแจ้งเตือน/คำอธิบายแบบมีขอบสี (คำเตือนก่อนยืนยัน, หมายเหตุใน modal, สถานะผลลัพธ์)
 * ใช้แทนการเขียน div bg-amber-50 border-amber-200 ... ซ้ำ ๆ
 */
const UiAlert: React.FC<UiAlertProps> = ({ tone = 'amber', icon, iconClassName = '', className = '', children }) => {
  const resolvedIcon = icon === null ? null : icon ?? DEFAULT_ICON[tone];
  return (
    <div className={`flex items-start gap-2 border rounded-xl px-4 py-3 text-sm ${TONE_CLASS[tone]} ${className}`}>
      {resolvedIcon && <FontAwesomeIcon icon={resolvedIcon} className={`mt-0.5 shrink-0 ${iconClassName}`} />}
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
};

export default UiAlert;
